/**
 * Wave B — view, display, workplane and UI commands.
 * (view.* / vis.* / sys.* UI entries; docs/architecture/command-parity-plan.md)
 */

import { runInteraction, PT, solidEntity } from './lib';
import { undoableAdd } from '../entityOps';
import { useAppStore } from '../../../state/store';
import { useViewStore } from '../../../state/commandAuxStores';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import { captureViewportPng } from '../../../services/mcp/viewportCapture';
import i18n from '../../../i18n';

function setView(name: string): void {
    window.dispatchEvent(new CustomEvent('yap:set-view', { detail: { view: name } }));
}

export const viewRunners: RunnerMap = {
    'view.standard': (args) => {
        const name = (args?.[0] ?? 'Top').toLowerCase();
        const map: Record<string, string> = { top: 'top', bottom: 'bottom', front: 'front', back: 'back', right: 'right', left: 'left', persp: 'perspective', perspective: 'perspective' };
        if (map[name]) { setView(map[name]); return `View set: ${name}.`; }
        runInteraction({
            steps: [{ prompt: 'VIEW: enter view name (Top/Front/Right/Perspective)', kind: 'text' }],
            complete: (_p, values) => {
                const v = String(values[0] ?? 'top').toLowerCase();
                if (map[v]) { setView(map[v]); return `View set: ${v}.`; }
                return `VIEW: unknown name "${v}".`;
            },
        });
        return 'VIEW: specify name';
    },
    'view.namedViews': (args) => {
        const sub = (args?.[0] ?? 'list').toLowerCase();
        const store = useViewStore.getState();
        if (sub === 'save' || args?.[0]?.toLowerCase() === 's') {
            runInteraction({
                steps: [{ prompt: 'NAMEVIEW: save current view as', kind: 'text' }],
                complete: (_p, values) => {
                    const v = useAppStore.getState().view;
                    store.saveView({
                        name: String(values[0] ?? `View-${Date.now()}`),
                        position: [...v.camera.position] as [number, number, number],
                        target: [...v.camera.target] as [number, number, number],
                    });
                    return `Named view "${values[0]}" saved.`;
                },
            });
            return 'NAMEVIEWS: enter name';
        }
        if (sub === 'list' || args?.length === 0) {
            const list = useViewStore.getState().namedViews;
            return list.length ? `Named views: ${list.map((n) => n.name).join(', ')}` : 'No named views yet (VIEW > Save).';
        }
        const restore = store.restoreView(sub);
        if (restore) {
            useAppStore.getState().updateView({
                camera: { ...useAppStore.getState().view.camera, position: restore.position, target: restore.target },
            });
            setView('perspective');
            return `Restored view "${sub}".`;
        }
        return `VIEW: no saved view "${sub}".`;
    },
    'view.plan': () => { setView('top'); return 'Plan view (current workplane).'; },
    'view.cplaneAuto': () => {
        useAppStore.getState().setActiveTool('SET_WORK_PLANE');
        return 'AUTOCPANE: workplane picking armed — click a face/object.';
    },
    'view.polar': () => {
        const s = useViewStore.getState();
        s.set('polarOn', !s.polarOn);
        notify(i18n.t('tools.notify.polarToggled', { state: !s.polarOn ? i18n.t('tools.notify.stateOnUpper') : i18n.t('tools.notify.stateOffUpper') }), 'info');
        return `Polar tracking ${!s.polarOn ? 'ON' : 'OFF'}.`;
    },
    'view.isodraft': (args) => {
        const plane = (args?.[0] ?? 'top').toLowerCase() as 'left' | 'top' | 'right' | 'off';
        useViewStore.getState().set('isoplane', plane);
        return `Isodraft plane: ${plane.toUpperCase()}.`;
    },
    'view.smartTrack': () => {
        const s = useViewStore.getState();
        s.set('smartTrack', !s.smartTrack);
        return `SmartTrack ${!s.smartTrack ? 'ON' : 'OFF'}.`;
    },
    'view.workspace': (args) => {
        const mode = (args?.[0] ?? 'drafting').toLowerCase();
        const map: Record<string, string> = { drafting: '2d', '2d': '2d', annotation: '2d', '3d': '3d', modeling: '3d', bim: 'bim', architecture: 'bim', sketch: 'sketch', freeform: 'sketch' };
        const target = map[mode];
        if (!target) return `Workspace "${mode}" unknown (drafting/annotation, 3D modeling, architecture).`;
        window.dispatchEvent(new CustomEvent('yap:set-workspace', { detail: { workspace: target } }));
        return `Workspace switched to "${mode}".`;
    },
    'view.toolPalette': () => {
        notify(i18n.t('tools.notify.toolPalettesEnableTheSidebarLayers'), 'info');
        return 'Tool Palettes: sidebar opened as palette dock.';
    },
    'view.turntable': () => {
        const s = useViewStore.getState();
        s.set('turntable', !s.turntable);
        window.dispatchEvent(new CustomEvent('yap:turntable', { detail: { on: !s.turntable } }));
        return `Turntable ${!s.turntable ? 'ON (click/Esc to stop)' : 'OFF'}.`;
    },
    'view.camera': () => {
        useAppStore.getState().setActiveTool('VIEW_CAMERA');
        return 'CAMERA: placement armed — click eye then target.';
    },
    'vis.render': () => {
        // Render-to-PNG of the current viewport. Routed through the MCP
        // capture bridge (it forces a synchronous render and reads the buffer
        // inside the same frame task); a bare canvas.toDataURL would grab a
        // blank frame now that preserveDrawingBuffer is off.
        const canvas = document.querySelector('canvas');
        if (!canvas) return 'RENDER: no viewport canvas.';
        notify(i18n.t('tools.notify.renderRequestedExportingViewportImage'), 'info');
        captureViewportPng().then((base64) => {
            const a = document.createElement('a');
            a.href = `data:image/png;base64,${base64}`;
            a.download = `yap-render-${Date.now()}.png`;
            a.click();
            notify(i18n.t('tools.notify.renderSaved'), 'success');
        }).catch(() => {
            notify(i18n.t('tools.notify.renderViewportCaptureFailed'), 'warning');
        });
        return 'RENDER: viewport captured to PNG download.';
    },
    'vis.light': () => {
        runInteraction({
            steps: [PT('LIGHT: position'), PT('LIGHT: target (direction)')],
            complete: (pts) => {
                // Real visible marker: a small luminous diamond octahedron
                // (0-index meshes rendered nothing — that was a flag-only
                // lie). Shadows remain a view-mode toggle.
                const p = [pts[0].x, pts[0].y, pts[0].z] as [number, number, number];
                const s = 0.15;
                const vertices = [
                    [p[0], p[1], p[2] + s], [p[0], p[1], p[2] - s],
                    [p[0] + s, p[1], p[2]], [p[0] - s, p[1], p[2]],
                    [p[0], p[1] + s, p[2]], [p[0], p[1] - s, p[2]],
                ];
                const indices = [
                    [0, 2, 4], [0, 4, 3], [0, 3, 5], [0, 5, 2],
                    [1, 4, 2], [1, 3, 4], [1, 5, 3], [1, 2, 5],
                ];
                undoableAdd([solidEntity({ vertices, indices }, `Light-${Date.now()}`, { color: '#ffee66' })]);
                useAppStore.getState().updateView({ shadowsEnabled: true });
                return 'Point light placed (shadow rendering enabled).';
            },
        });
        return 'LIGHT: specify position';
    },
    'vis.styles': () => {
        const order = ['wireframe', 'shaded', 'rendered', 'xray', 'ghosted', 'hiddenLine'] as const;
        const app = useAppStore.getState();
        const idx = order.indexOf(app.view.viewMode as typeof order[number]);
        app.updateView({ viewMode: order[(idx + 1) % order.length] });
        return `Display mode: ${order[(idx + 1) % order.length]}.`;
    },
    'view.pan': () => {
        runInteraction({
            steps: [PT('PAN: base point'), PT('PAN: target point (or Enter for middle-mouse drag)')],
            complete: (pts) => {
                const dx = pts[1].x - pts[0].x, dy = pts[1].y - pts[0].y;
                window.dispatchEvent(new CustomEvent('yap:pan-by', { detail: { dx, dy } }));
                return `Panned by ${dx.toFixed(2)}, ${dy.toFixed(2)}.`;
            },
        });
        return 'PAN: specify base point (middle-mouse drag also pans).';
    },
    'sys.menuBar': () => {
        const s = useViewStore.getState();
        s.set('ribbonVisible', !s.ribbonVisible);
        window.dispatchEvent(new CustomEvent('yap:ui-toggle', { detail: { ribbon: !s.ribbonVisible } }));
        return `Menu/Ribbon bar ${!s.ribbonVisible ? 'ON' : 'OFF'}.`;
    },
    'sys.ribbonToggle': () => (viewRunners['sys.menuBar'] as () => string)(),
    'sys.history': () => 'Command history shown in the command line panel (scroll up).',
    'sys.options': () => {
        useAppStore.getState().openManageDialog('commandPresets');
        return 'OPTIONS: opening settings hub (Command Presets).';
    },
    'sys.settings': () => 'Settings: see File > Settings for units/appearance.',
};


